/**
 * "Category order" — the order of the category tabs at the top of the dealer
 * app. Reads `app_config/global.categories` (published by the sync) and writes
 * ONLY `app_config/global.categoryOrder`, a field the sync never touches, so a
 * re-sync can't undo it. The effective order shown here comes from the same
 * shared orderCategories() the dealer app renders with.
 *
 * Up/down buttons (big enough for a phone) — no drag-and-drop needed.
 */
import { useEffect, useMemo, useState } from 'react';
import { onSnapshot, setDoc } from 'firebase/firestore';
import { orderCategories } from '@mitang/shared';
import { appConfigDoc } from '../lib/collections';
import { useToast } from '../lib/toast';
import { Button, Card } from './ui';
import { ErrorState, LoadingState } from './States';

export function CategoryOrderCard(): JSX.Element {
  const toast = useToast();
  const [categories, setCategories] = useState<string[] | null>(null);
  const [savedOrder, setSavedOrder] = useState<string[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Local edits; null = nothing changed (show the saved/effective order). */
  const [draft, setDraft] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      onSnapshot(
        appConfigDoc,
        (snap) => {
          const data = snap.data();
          setCategories(data?.categories ?? []);
          setSavedOrder(data?.categoryOrder);
          setLoadError(null);
        },
        (err) => setLoadError(err.message),
      ),
    [],
  );

  const effective = useMemo(
    () => orderCategories(categories ?? [], savedOrder),
    [categories, savedOrder],
  );
  // A draft only holds names that still exist; reconcile it if the sync
  // adds/removes a tab while the admin is editing.
  const list = useMemo(
    () => (draft ? orderCategories(categories ?? [], draft) : effective),
    [draft, categories, effective],
  );
  const dirty = draft !== null && list.join('\u0000') !== effective.join('\u0000');

  const move = (from: number, to: number) => {
    if (to < 0 || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setDraft(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      // merge: touches only categoryOrder (arrays are replaced, not merged).
      await setDoc(appConfigDoc, { categoryOrder: list }, { merge: true });
      setDraft(null);
      toast.success('Category order saved — dealers see it right away.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
        <span className="text-sm font-semibold text-slate-700">Category order</span>
        {!savedOrder || savedOrder.length === 0 ? (
          <span className="text-xs text-slate-400">Currently alphabetical</span>
        ) : null}
      </div>
      <div className="px-5 py-2 text-xs text-slate-400">
        The order of the tabs at the top of the dealer app — the first one opens by default. New
        categories from the spreadsheet appear at the end until you move them.
      </div>
      <div className="px-5 pb-4">
        {loadError ? (
          <ErrorState message={loadError} />
        ) : categories === null ? (
          <LoadingState label="Loading categories…" />
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-400">No categories synced yet.</p>
        ) : (
          <>
            <ol className="divide-y divide-slate-50 rounded-lg border border-slate-100">
              {list.map((name, i) => (
                <li key={name} className="flex items-center gap-2 px-3 py-1.5">
                  <span className="w-6 text-right text-xs tabular-nums text-slate-400">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-800" title={name}>
                    {name}
                  </span>
                  <button
                    type="button"
                    className="rounded px-3 py-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0 || busy}
                    aria-label={`Move ${name} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="rounded px-3 py-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    onClick={() => move(i, i + 1)}
                    disabled={i === list.length - 1 || busy}
                    aria-label={`Move ${name} down`}
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ol>
            <div className="mt-3 flex gap-2">
              <Button variant="primary" onClick={() => void save()} disabled={!dirty} loading={busy}>
                Save order
              </Button>
              {dirty ? (
                <Button variant="secondary" onClick={() => setDraft(null)} disabled={busy}>
                  Discard
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
